/**
 * Helpers to surface backend (ABP-style) validation errors on Formik forms.
 *
 * The OpenAPI-generated clients throw a `ResponseError` whose `.response` is the
 * raw fetch `Response` — the JSON body is NOT parsed for us. These helpers read
 * that body once and map each `validationErrors[].members[]` path onto the
 * matching Formik field so it turns red, instead of only showing a generic toast.
 */

export interface ApiValidationError {
  message: string;
  members: string[];
}

export interface ParsedApiError {
  message?: string;
  details?: string;
  code?: string;
  validationErrors: ApiValidationError[];
}

const EMPTY_PARSED: ParsedApiError = { validationErrors: [] };

/**
 * Le back lève souvent `BusinessException("texte métier")` : ABP range alors le texte
 * dans `code` et met un message générique dans `message`. Quand c'est le cas, on
 * affiche le texte du `code` s'il ressemble à une phrase (les vrais codes techniques,
 * type "Volo.Abp:010001", n'ont pas d'espace) — sinon l'utilisateur ne voit
 * qu'« une erreur interne s'est produite » alors que le back a un message clair.
 */
const GENERIC_API_MESSAGES = [
  'une erreur interne',
  'an internal error'
];

function resolveUserMessage(message?: string, code?: string): string | undefined {
  const isGeneric =
    !message ||
    GENERIC_API_MESSAGES.some((generic) =>
      message.toLowerCase().includes(generic)
    );
  const codeIsReadable =
    typeof code === 'string' && code.trim().includes(' ');

  return isGeneric && codeIsReadable ? code : message;
}

/**
 * Extract the structured error envelope (`{ error: { message, validationErrors } }`)
 * from a thrown API error. Tolerant of both the raw `ResponseError` (where the body
 * still needs parsing) and an already-parsed shape. Never throws.
 */
export async function parseApiError(error: unknown): Promise<ParsedApiError> {
  const response = (error as { response?: unknown })?.response;
  if (!response) return EMPTY_PARSED;

  try {
    let body: any = response;

    // Raw fetch Response (ResponseError) — clone before reading so we don't
    // consume the body other consumers might rely on, and to bypass the
    // notification-wrapping proxy installed by the auth interceptor.
    if (typeof (response as Response).clone === 'function') {
      body = await (response as Response).clone().json();
    } else if (body && typeof body === 'object' && 'data' in body) {
      // Erreur axios : le JSON déjà parsé vit dans `response.data`.
      body = (body as { data: unknown }).data;
    }

    const err = body?.error ?? body;
    if (!err || typeof err !== 'object') return EMPTY_PARSED;

    return {
      message: resolveUserMessage(err.message, err.code),
      details: err.details,
      code: err.code,
      validationErrors: Array.isArray(err.validationErrors)
        ? err.validationErrors
        : []
    };
  } catch {
    return EMPTY_PARSED;
  }
}

/**
 * Resolver for forms whose address autocomplete is bound to a single (flat)
 * Formik field. Maps any address / place_id member onto that field, plus a few
 * common person fields. `addressField` defaults to `'address'`.
 */
export const createPersonFieldResolver =
  (addressField = 'address') =>
  (member: string): string | undefined => {
    const m = member.toLowerCase();
    if (
      m.includes('address') ||
      m.includes('adresse') ||
      m.includes('googleplaceid') ||
      m.includes('placeid')
    ) {
      return addressField;
    }
    if (m.includes('email')) return 'email';
    if (m.includes('prenom') || m.includes('firstname')) return 'firstName';
    if (m.includes('lastname') || m.includes('nom')) return 'lastName';
    if (m.includes('birthdate') || m.includes('naissance')) return 'birthDate';
    if (m.includes('civilit')) return 'civility';
    if (m.includes('capital')) return 'capital';
    if (m.includes('siret') || m.includes('registrationnumber')) return 'registrationNumber';
    if (m.includes('rcsville') || m.includes('rcscity')) return 'rcsCity';
    if (m.includes('rcsnumero') || m.includes('rcsnumber')) return 'rcsNumber';
    if (m.includes('orias')) return 'oriasNumber';
    if (m.includes('telephone') || m.includes('phone')) return 'phone';
    if (m.includes('designation') || m.includes('companyname')) return 'companyName';
    return undefined;
  };

export interface ApplyValidationErrorsOptions {
  validationErrors: ApiValidationError[];
  setFieldError: (field: string, message: string | undefined) => void;
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => void;
  /**
   * Map a backend member path (e.g. `"naturalPerson.address.googlePlaceId"`)
   * to a Formik field name. Return `undefined` to leave that member unmapped
   * (it will still surface via the toast message).
   */
  resolveField: (member: string) => string | undefined;
}

/**
 * Paint backend validation errors onto Formik fields (marks them touched + sets
 * the error so they render red). Returns how many fields were matched, so the
 * caller can decide what to put in the toast.
 */
export function applyValidationErrorsToForm({
  validationErrors,
  setFieldError,
  setFieldTouched,
  resolveField
}: ApplyValidationErrorsOptions): number {
  let matched = 0;

  for (const validationError of validationErrors) {
    const members = validationError.members?.length
      ? validationError.members
      : [];

    for (const member of members) {
      const field = resolveField(member);
      if (!field) continue;

      setFieldTouched(field, true, false);
      setFieldError(field, validationError.message);
      matched += 1;
    }
  }

  return matched;
}
